import type { LucideIcon } from 'lucide-react'

import { BodyText } from '@/components/core/BodyText'
import { Button } from '@/components/core/Button'
import type { ButtonVariant } from '@/components/core/Button/buttonClassName'
import { ButtonLink } from '@/components/core/ButtonLink'

import './index.scss'

/** What the outcome means to the reader; it colours the icon. */
export type StatusScreenTone = 'error' | 'success' | 'neutral'

/** A link that becomes the screen's main action, e.g. back to where the user came from. */
export interface StatusScreenMainLink {
  href: string
  label: string
  /** A logo before the label, e.g. the assistant's. */
  iconSrc?: string
}

export interface StatusScreenProps {
  tone: StatusScreenTone
  icon: LucideIcon
  message: string
  /** The button's onClick. Generic: may retry, navigate away, etc. */
  onAction: () => void
  actionLabel: string
  /** Optional heading above the message. */
  title?: string
  /** Layout footprint. @default 'fullpage' */
  variant?: 'fullpage' | 'inline' | 'fill'
  /** Palette. 'dark' matches dark routes ($bg-dark). @default 'light' */
  theme?: 'light' | 'dark'
  /** Shown above the button as the main action; the button then turns secondary. */
  mainLink?: StatusScreenMainLink
}

// The secondary button per palette, as the dark feedback pages pair it with a primary-light one.
const SECONDARY_VARIANT: Record<NonNullable<StatusScreenProps['theme']>, ButtonVariant> = {
  light: 'outline',
  dark: 'outline-on-dark',
}

/** A full-screen outcome: an icon in the outcome's tone, an optional title, a message and its action(s). */
export function StatusScreen({
  tone,
  icon: Icon,
  message,
  onAction,
  actionLabel,
  title,
  variant = 'fullpage',
  theme = 'light',
  mainLink,
}: StatusScreenProps) {
  const buttonSize = title ? 'medium' : 'small'
  return (
    <div className={`status-screen status-screen--${variant} status-screen--${theme} status-screen--${tone}`}>
      <div className="status-screen__content">
        <Icon className="status-screen__icon" size={40} strokeWidth={2} aria-hidden />
        {title && <h2 className="status-screen__title">{title}</h2>}
        <BodyText className="status-screen__message">{message}</BodyText>
        <div className="status-screen__actions">
          {mainLink && (
            <ButtonLink href={mainLink.href} variant="primary-light" size={buttonSize}>
              {mainLink.iconSrc && <img className="status-screen__link-icon" src={mainLink.iconSrc} alt="" />}
              {mainLink.label}
            </ButtonLink>
          )}
          <Button variant={mainLink ? SECONDARY_VARIANT[theme] : 'primary-light'} size={buttonSize} onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
